import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, CalendarDays, MonitorSpeaker, IndianRupee, Clock,
  CreditCard, MessageCircle, Users, LogOut, Menu, X, Loader2, Activity, Gamepad2,
  Package, ClipboardList, QrCode, ReceiptText, FileSpreadsheet, Ban, Tv,
} from "lucide-react";
import { api } from "@/lib/api";
import { LOGO_URL } from "@/lib/assets";
import OverviewTab from "@/components/admin/OverviewTab";
import LiveTab from "@/components/admin/LiveTab";
import BookingsTab from "@/components/admin/BookingsTab";
import CancelledTab from "@/components/admin/CancelledTab";
import StationsTab from "@/components/admin/StationsTab";
import GamesTab from "@/components/admin/GamesTab";
import InventoryTab from "@/components/admin/InventoryTab";
import OrdersTab from "@/components/admin/OrdersTab";
import QrTab from "@/components/admin/QrTab";
import BillsTab from "@/components/admin/BillsTab";
import ReportsTab from "@/components/admin/ReportsTab";
import PricingTab from "@/components/admin/PricingTab";
import HoursTab from "@/components/admin/HoursTab";
import PaymentTab from "@/components/admin/PaymentTab";
import WhatsappTab from "@/components/admin/WhatsappTab";
import CustomersTab from "@/components/admin/CustomersTab";
import LivePreviewTab from "@/components/admin/LivePreviewTab";
import AdminBooking from "@/components/admin/AdminBooking";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard, C: OverviewTab },
  { id: "newbooking", label: "New Booking", icon: CalendarDays, C: AdminBooking },
  { id: "live", label: "Live Monitor", icon: Activity, C: LiveTab },
  { id: "display", label: "Live Preview", icon: Tv, C: LivePreviewTab },
  { id: "bookings", label: "Bookings", icon: CalendarDays, C: BookingsTab },
  { id: "cancelled", label: "Cancelled", icon: Ban, C: CancelledTab },
  { id: "orders", label: "Food Orders", icon: ClipboardList, C: OrdersTab },
  { id: "inventory", label: "Inventory", icon: Package, C: InventoryTab },
  { id: "qr", label: "QR Codes", icon: QrCode, C: QrTab },
  { id: "bills", label: "Bills", icon: ReceiptText, C: BillsTab },
  { id: "reports", label: "Reports", icon: FileSpreadsheet, C: ReportsTab },
  { id: "stations", label: "Stations", icon: MonitorSpeaker, C: StationsTab },
  { id: "games", label: "Games", icon: Gamepad2, C: GamesTab },
  { id: "pricing", label: "Pricing", icon: IndianRupee, C: PricingTab },
  { id: "hours", label: "Time & Hours", icon: Clock, C: HoursTab },
  { id: "payment", label: "Payment", icon: CreditCard, C: PaymentTab },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, C: WhatsappTab },
  { id: "customers", label: "Customers", icon: Users, C: CustomersTab },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [active, setActive] = useState("overview");
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem("psgc_token")) { nav("/admin"); return; }
    api.get("/admin/me").then(() => setChecking(false)).catch(() => { localStorage.removeItem("psgc_token"); nav("/admin"); });
  }, [nav]);

  const logout = () => { localStorage.removeItem("psgc_token"); nav("/admin"); };

  useEffect(() => {
    const h = (e) => { setActive(e.detail); setOpen(false); };
    window.addEventListener("psgc-nav", h);
    return () => window.removeEventListener("psgc-nav", h);
  }, []);

  if (checking) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-purple-400" size={40} /></div>;

  const Active = TABS.find((t) => t.id === active).C;

  return (
    <div className="min-h-screen flex">
      <aside className={`fixed lg:static z-40 h-full w-64 bg-purple-950/60 backdrop-blur-xl border-r border-purple-500/20 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 flex items-center gap-2 border-b border-purple-500/20">
          <img src={LOGO_URL} alt="" className="w-9 h-9 rounded-lg object-cover" />
          <span className="font-display font-bold text-sm tracking-wider">ADMIN PANEL</span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => { setActive(t.id); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-medium transition-all ${active === t.id ? "bg-purple-600 text-white" : "text-slate-400 hover:bg-purple-900/40 hover:text-white"}`}>
              <t.icon size={18} /> {t.label}
            </button>
          ))}
        </nav>
        <button data-testid="admin-logout-btn" onClick={logout} className="m-3 flex items-center gap-3 px-3 h-11 rounded-xl text-sm text-red-300 hover:bg-red-950/40">
          <LogOut size={18} /> Logout
        </button>
      </aside>

      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 backdrop-blur-md bg-purple-950/50 border-b border-purple-500/20 h-16 flex items-center px-4 gap-3">
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="sidebar-toggle">{open ? <X /> : <Menu />}</button>
          <h1 className="font-display font-bold tracking-wide">{TABS.find((t) => t.id === active).label}</h1>
        </header>
        <div className="p-4 sm:p-6 max-w-6xl">
          <Active />
        </div>
      </main>
    </div>
  );
}
